import { useEffect, useState } from "react";

// AgentAssembly is the "we're building your agent" surface shown while a new
// agent's isolated microVM is provisioned. A blocky, Minecraft-style humanoid
// snaps together piece by piece on a lit platform, with a build-scan sweep and
// cycling status lines. Pure CSS (no three.js) so it's instant + light. It fills
// its parent's width and centers the scene — drop it inside the SAME container
// the form used so the page width never changes.

const STEPS = [
  "Allocating an isolated sandbox…",
  "Booting the microVM…",
  "Mounting the workspace…",
  "Loading the playbook…",
  "Wiring up the /mcp endpoint…",
  "Waking your agent…",
];

// Minecraft palette.
const SKIN = "#c98c5a";
const SKIN_DK = "#b07a4c";
const SHIRT = "#A6ABFF"; // brand periwinkle
const SHIRT_DK = "#7c82f0";
const PANTS = "#3a3f52";
const PANTS_DK = "#2c303f";

// blk is one voxel block: a hard-edged box with a darker bottom/right via an
// inset shadow for a faux-3D Minecraft look. `delay` staggers its snap-in.
function blk(
  bg: string,
  shade: string,
  style: React.CSSProperties,
  delay: number,
  extra?: React.CSSProperties,
): React.CSSProperties {
  return {
    position: "absolute",
    background: bg,
    boxShadow: `inset -4px -5px 0 0 ${shade}`,
    animation: `aa-snap 520ms cubic-bezier(.22,1.5,.4,1) both`,
    animationDelay: `${delay}ms`,
    imageRendering: "pixelated",
    ...style,
    ...extra,
  };
}

export function AgentAssembly({ name }: { name?: string }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 1500);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center py-10 text-center sm:py-16">
      <style>{KEYFRAMES}</style>

      <div className="relative h-64 w-56 select-none" aria-hidden>
        {/* platform + drop shadow */}
        <div
          className="absolute left-1/2 bottom-6 -translate-x-1/2"
          style={{
            width: 150,
            height: 26,
            background: "radial-gradient(ellipse at center, rgba(61,90,254,.28), transparent 70%)",
            animation: "aa-shadow 2.8s ease-in-out infinite",
          }}
        />
        <div
          className="absolute left-1/2 bottom-7 -translate-x-1/2"
          style={{ width: 132, height: 12, background: "#1b2030", boxShadow: "inset -6px -6px 0 0 #11141f" }}
        />

        {/* the character bobs gently once assembled */}
        <div className="absolute inset-0" style={{ animation: "aa-bob 2.8s ease-in-out 1.3s infinite" }}>
          {/* legs */}
          <div style={blk(PANTS, PANTS_DK, { width: 26, height: 52, left: 88, top: 168 }, 0)} />
          <div style={blk(PANTS, PANTS_DK, { width: 26, height: 52, left: 118, top: 168 }, 80)} />
          {/* arms */}
          <div style={blk(SKIN, SKIN_DK, { width: 22, height: 70, left: 62, top: 96 }, 360)}>
            <div style={{ position: "absolute", inset: 0, height: 30, background: SHIRT, boxShadow: `inset -4px -4px 0 0 ${SHIRT_DK}` }} />
          </div>
          <div style={blk(SKIN, SKIN_DK, { width: 22, height: 70, left: 148, top: 96 }, 460)}>
            <div style={{ position: "absolute", inset: 0, height: 30, background: SHIRT, boxShadow: `inset -4px -4px 0 0 ${SHIRT_DK}` }} />
          </div>
          {/* torso */}
          <div style={blk(SHIRT, SHIRT_DK, { width: 64, height: 74, left: 86, top: 96 }, 240)}>
            {/* glowing AI core */}
            <div
              style={{
                position: "absolute",
                left: 22,
                top: 26,
                width: 20,
                height: 20,
                background: "#cdd6ff",
                boxShadow: "0 0 14px 3px rgba(160,180,255,.9)",
                animation: "aa-core 1.4s ease-in-out infinite",
              }}
            />
          </div>
          {/* head */}
          <div style={blk(SKIN, SKIN_DK, { width: 64, height: 60, left: 86, top: 34 }, 620)}>
            {/* eyes (glowing indigo = "AI") */}
            <div style={{ position: "absolute", left: 14, top: 26, width: 10, height: 12, background: "#fff" }}>
              <div style={{ position: "absolute", right: 0, top: 0, width: 6, height: 12, background: SHIRT, animation: "aa-blink 3.4s steps(1) infinite" }} />
            </div>
            <div style={{ position: "absolute", left: 40, top: 26, width: 10, height: 12, background: "#fff" }}>
              <div style={{ position: "absolute", right: 0, top: 0, width: 6, height: 12, background: SHIRT, animation: "aa-blink 3.4s steps(1) infinite" }} />
            </div>
            {/* mouth */}
            <div style={{ position: "absolute", left: 22, top: 46, width: 20, height: 5, background: SKIN_DK }} />
          </div>
        </div>

        {/* build-scan sweep */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{
            bottom: 18,
            width: 140,
            height: 8,
            background: "linear-gradient(90deg, transparent, rgba(120,150,255,.85), transparent)",
            filter: "blur(1px)",
            animation: "aa-scan 2.6s ease-in-out infinite",
          }}
        />

        {/* rising build pixels */}
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: 40 + i * 32,
              bottom: 28,
              width: 7,
              height: 7,
              background: i % 2 ? SHIRT : "#c4c8ff",
              animation: `aa-rise 2.4s ease-in ${i * 380}ms infinite`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      <h2 className="mt-2 text-lg font-semibold tracking-tight">
        Assembling{name ? ` ${name}` : " your agent"}
      </h2>
      <p className="mt-1 h-5 text-sm text-muted-foreground transition-all" key={step}>
        {STEPS[step]}
      </p>
      <p className="mt-3 text-xs text-muted-foreground/70">This usually takes a few seconds.</p>
    </div>
  );
}

const KEYFRAMES = `
@keyframes aa-snap {
  0%   { opacity: 0; transform: translateY(-26px) scaleY(.25); }
  100% { opacity: 1; transform: translateY(0) scaleY(1); }
}
@keyframes aa-bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-5px); }
}
@keyframes aa-core {
  0%, 100% { opacity: .55; box-shadow: 0 0 8px 1px rgba(160,180,255,.6); }
  50%      { opacity: 1;   box-shadow: 0 0 18px 5px rgba(160,180,255,1); }
}
@keyframes aa-blink {
  0%, 92%, 100% { opacity: 1; }
  95%           { opacity: 0; }
}
@keyframes aa-shadow {
  0%, 100% { transform: translateX(-50%) scaleX(1);   opacity: .8; }
  50%      { transform: translateX(-50%) scaleX(.82); opacity: .5; }
}
@keyframes aa-scan {
  0%   { transform: translateX(-50%) translateY(0);     opacity: 0; }
  15%  { opacity: 1; }
  85%  { opacity: 1; }
  100% { transform: translateX(-50%) translateY(-150px); opacity: 0; }
}
@keyframes aa-rise {
  0%   { opacity: 0; transform: translateY(0); }
  20%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-90px); }
}
@media (prefers-reduced-motion: reduce) {
  [style*="aa-"] { animation: none !important; }
}
`;
